import styles from 'css/src/exports.module.scss';

import cloneDeep from 'lodash/cloneDeep';
import type { MediaSourceData } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import {
    AssetTypes,
    LanguageEnum,
    LicenseEnum,
    NarrativeThreadEnum,
    RoleEnum,
    VideoApprovalStatusEnum,
    VideoStatus,
} from 'js/src/client';
import { openCrispChat } from 'js/src/libs/crisp';

export const MAX_SCENE_GROUPS_IN_VIDEO = 100;

// Script composer consts
export const MAX_SENTENCES_FOR_USE_ALL_SENTENCES = 40;
export const MIN_WORDS_PER_EDITED_SCRIPT_BLOCK = 15;
export const WORD_DURATION_ESTIMATE_SECONDS = 0.307;

// The subgroup of video statuses corresponding to 'rendering'
export const RENDERING_STATUSES = [VideoStatus.QUEUED, VideoStatus.PROCESSING, VideoStatus.FAILED];

export const FREE_PLAN_NAME = 'Free'; // derived from `web/models.py`: `get_plan_name_for_analytics`

// Derived from the web/pricing/pricing_plans.py
export const PROMOTION_PLANS_DISPLAY_NAMES = ['Promo Plan 2020', 'AppSumo Designer Plan 2021'];
export const FREE_PLAN_DISPLAY_NAME = 'Community';

// Pricing plan API name prefixes
export const PLAN_PREFIX = {
    BUSINESS: 'business',
    STARTER: 'starter',
    ENTERPRISE: 'team',
    PREMIUM: 'premium',
    PRO: 'pro',
};

export const PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY = {
    UPGRADE_LEVEL_1: 'Basic',
    UPGRADE_LEVEL_2: 'Starter',
    UPGRADE_LEVEL_3: 'Pro',
} as const;

export type PrettyPlanName =
    (typeof PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY)[keyof typeof PRETTY_PLAN_NAMES_FOR_WEBSITE_COPY];

export type FileFormat = {
    mimeType: string;
    extension: string;
    userUploadable: boolean;
    uploadDisplayName?: string;
    isAnimatedGif?: boolean;
};

type FileFormatsMasterList = Record<AssetTypes, FileFormat[]>;

const VALID_IMAGE_ONLY_FILE_FORMATS: FileFormat[] = [
    {
        mimeType: 'image/png',
        extension: 'PNG',
        userUploadable: true,
    },
    {
        mimeType: 'image/jpeg',
        extension: 'JPG',
        userUploadable: true,
    },
    {
        mimeType: 'image/jpg',
        extension: 'JPG',
        userUploadable: true,
    },
    {
        mimeType: 'image/bmp',
        extension: 'BMP',
        userUploadable: true,
    },
    {
        mimeType: 'image/x-windows-bmp',
        extension: 'BMP',
        userUploadable: true,
    },
    {
        mimeType: 'image/x-ms-bmp',
        extension: 'BMP',
        userUploadable: true,
    },
    {
        mimeType: 'image/webp',
        extension: 'WEBP',
        userUploadable: true,
    },
];

const VALID_IMAGE_FILE_FORMATS: FileFormat[] = [
    ...VALID_IMAGE_ONLY_FILE_FORMATS,
    {
        mimeType: 'image/gif',
        extension: 'GIF',
        userUploadable: true,
        isAnimatedGif: true,
    },
];

const VALID_VIDEO_FILE_FORMATS: FileFormat[] = [
    {
        mimeType: 'video/quicktime',
        extension: 'MOV',
        userUploadable: true,
    },
    {
        mimeType: 'image/mov',
        extension: 'MOV',
        userUploadable: true,
    },
    {
        mimeType: 'video/x-quicktime',
        extension: 'MOV',
        userUploadable: true,
    },
    {
        mimeType: 'video/webm',
        extension: 'WEBM',
        userUploadable: true,
    },
    {
        mimeType: 'video/webm;codecs=h264',
        extension: 'WEBM',
        userUploadable: true,
    },
    {
        mimeType: 'video/mp4',
        extension: 'MP4',
        userUploadable: true,
    },
    {
        mimeType: 'video/x-m4v',
        extension: 'M4V',
        userUploadable: true,
    },
    {
        mimeType: 'video/x-msvideo',
        extension: 'AVI',
        userUploadable: true,
    },
    {
        mimeType: 'video/3gpp',
        extension: '3GP',
        userUploadable: true,
    },
    {
        mimeType: 'video/x-flv',
        extension: 'FLV',
        userUploadable: true,
    },
    {
        mimeType: 'video/x-mkv',
        extension: 'MKV',
        userUploadable: true,
    },
    {
        mimeType: 'video/x-matroska',
        extension: 'MKV',
        userUploadable: true,
    },
    {
        mimeType: 'video/*,.mkv',
        extension: 'MKV',
        userUploadable: true,
    },
    {
        mimeType: 'video/avi',
        extension: 'AVI',
        userUploadable: true,
    },
    {
        mimeType: 'video/mpeg',
        extension: 'MPEG',
        userUploadable: true,
    },
    {
        mimeType: 'video/mpeg',
        extension: 'MPG',
        userUploadable: true,
    },
];

const VALID_AUDIO_FILE_FORMATS: FileFormat[] = [
    {
        mimeType: 'audio/mp3', // Chrome's mp3 mime type
        extension: 'MP3',
        userUploadable: true,
    },
    {
        mimeType: 'audio/mpeg', // Safari's mp3 mime type
        extension: 'MP3',
        userUploadable: true,
    },
    {
        mimeType: 'audio/webm', // Chrome/Opera media recorder default
        extension: 'WEBM',
        userUploadable: false,
    },
    {
        mimeType: 'audio/ogg', // Firefox media recorder default
        extension: 'OGG',
        userUploadable: false,
    },
    {
        mimeType: 'audio/mp4', // Safari's media recorder default
        extension: 'MP4',
        userUploadable: false,
    },
    {
        mimeType: 'audio/x-m4a', // m4a mime type
        extension: 'M4A',
        userUploadable: true,
    },
    {
        mimeType: 'audio/wav', // wav mime type
        extension: 'WAV',
        userUploadable: true,
    },
    {
        mimeType: 'audio/flac', // flac, open source audio codec
        extension: 'FLAC',
        userUploadable: true,
    },
];

const VALID_ICON_FILE_FORMATS: FileFormat[] = [
    {
        mimeType: 'image/svg+xml',
        extension: 'SVG',
        userUploadable: true,
    },
    {
        mimeType: 'application/json',
        extension: 'JSON',
        userUploadable: true,
        uploadDisplayName: 'Lottie JSON (https://lottiefiles.com)',
    },
];

const VALID_FONT_FILE_FORMATS: FileFormat[] = [
    {
        mimeType: 'font/ttf',
        extension: 'TTF',
        userUploadable: true,
    },
    {
        mimeType: 'application/x-font-ttf',
        extension: 'TTF',
        userUploadable: true,
    },
    {
        mimeType: 'application/x-font-truetype',
        extension: 'TTF',
        userUploadable: true,
    },
    {
        mimeType: 'font/otf',
        extension: 'OTF',
        userUploadable: true,
    },
    {
        mimeType: 'application/x-font-otf',
        extension: 'OTF',
        userUploadable: true,
    },
    {
        mimeType: 'application/x-font-opentype',
        extension: 'OTF',
        userUploadable: true,
    },
];

const VALID_DOCUMENT_FILE_FORMATS: FileFormat[] = [
    {
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        extension: 'DOCX',
        userUploadable: true,
    },
    {
        mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        extension: 'PPTX',
        userUploadable: true,
    },
    {
        mimeType: 'application/msword',
        extension: 'DOC',
        userUploadable: true,
    },
    {
        mimeType: 'application/pdf',
        extension: 'PDF',
        userUploadable: true,
    },
    {
        mimeType: 'text/xml',
        extension: 'XML',
        userUploadable: true,
    },
    {
        mimeType: 'application/xml',
        extension: 'XML',
        userUploadable: true,
    },
];

const VALID_FILE_FORMATS: FileFormatsMasterList = {
    [AssetTypes.IMAGE]: VALID_IMAGE_FILE_FORMATS,
    [AssetTypes.VIDEO]: VALID_VIDEO_FILE_FORMATS,
    [AssetTypes.AUDIO]: VALID_AUDIO_FILE_FORMATS,
    [AssetTypes.ICON]: VALID_ICON_FILE_FORMATS,
    [AssetTypes.FONT]: VALID_FONT_FILE_FORMATS,
    [AssetTypes.DOCUMENT]: VALID_DOCUMENT_FILE_FORMATS,
};

export function getValidImageFileFormats(onlyImages = false): FileFormat[] {
    return onlyImages ? VALID_IMAGE_ONLY_FILE_FORMATS : VALID_IMAGE_FILE_FORMATS;
}

export function getValidVideoFileFormats(): FileFormat[] {
    return VALID_VIDEO_FILE_FORMATS;
}

export function getValidAudioFileFormats(): FileFormat[] {
    return VALID_AUDIO_FILE_FORMATS;
}

export function getValidIconFileFormats(): FileFormat[] {
    return VALID_ICON_FILE_FORMATS;
}

export function getValidFontFileFormats(): FileFormat[] {
    return VALID_FONT_FILE_FORMATS;
}

export function getValidDocumentFileFormats(): FileFormat[] {
    return VALID_DOCUMENT_FILE_FORMATS;
}

export function getUserUploadableFileFormats(formats: FileFormat[]): FileFormat[] {
    return formats.filter((format) => format.userUploadable);
}

export function getUploadableImageFileFormats(onlyImages: boolean = false): FileFormat[] {
    return getUserUploadableFileFormats(getValidImageFileFormats(onlyImages));
}

export function getUploadableVideoFileFormats(): FileFormat[] {
    return getUserUploadableFileFormats(getValidVideoFileFormats());
}

export function getUploadableAudioFileFormats(): FileFormat[] {
    return getUserUploadableFileFormats(getValidAudioFileFormats());
}

export function getUploadableIconFileFormats(): FileFormat[] {
    return getUserUploadableFileFormats(getValidIconFileFormats());
}

export function getUploadableFontFileFormats(): FileFormat[] {
    return getUserUploadableFileFormats(getValidFontFileFormats());
}

export function getMimeTypesForFileFormats(formats: FileFormat[]): string[] {
    return [...new Set(formats.map((format) => format.mimeType))];
}

export function getExtensionNamesForFileFormats(formats: FileFormat[]): string[] {
    return [...new Set(formats.map((format) => format.extension))];
}

export function getDisplayNamesForFileFormats(formats: FileFormat[]): string[] {
    return [...new Set(formats.map((format) => format.uploadDisplayName || format.extension))];
}

export function GET_VALID_IMAGE_MIME_TYPES(only_images: boolean): string[] {
    return getMimeTypesForFileFormats(getValidImageFileFormats(only_images));
}

export function GET_VALID_VIDEO_MIME_TYPES(): string[] {
    return getMimeTypesForFileFormats(getValidVideoFileFormats());
}

export function GET_VALID_ICON_MIME_TYPES(): string[] {
    return getMimeTypesForFileFormats(getValidIconFileFormats());
}

export function GET_VALID_AUDIO_MIME_TYPES(): string[] {
    return getMimeTypesForFileFormats(getValidAudioFileFormats());
}

const UPLOADABLE_VIDEO_EXTENSION_NAMES = [
    ...new Set(
        getUploadableVideoFileFormats().map(
            (format) => format.uploadDisplayName || format.extension,
        ),
    ),
];
const UPLOADABLE_IMAGE_EXTENSION_NAMES = [
    ...new Set(
        getUploadableImageFileFormats(false).map(
            (format) => format.uploadDisplayName || format.extension,
        ),
    ),
];
const UPLOADABLE_AUDIO_EXTENSION_NAMES = [
    ...new Set(
        getUploadableAudioFileFormats().map(
            (format) => format.uploadDisplayName || format.extension,
        ),
    ),
];
const UPLOADABLE_ICON_EXTENSION_NAMES = [
    ...new Set(
        getUploadableIconFileFormats().map(
            (format) => format.uploadDisplayName || format.extension,
        ),
    ),
];

export type CommaSeparatedMimeTypes = string;

// Returns a string of uploadable formats for a given asset type and/or mime types.
// - If you pass in mime types, you should use a comma-separated string of mime types.
//   For example: "video/mp4, video/webm, image/png". You can
//   also specify wildcards like "video/*" or "image/*".
// - If you pass in an asset type, it will return the uploadable formats for that
//   asset type.
// - If you pass in both, returns the combination of both.
export function getUploadableFormatsMessage(
    assetTypes?: AssetTypes[],
    mimeTypes?: CommaSeparatedMimeTypes,
): string {
    const results = new Set<string>();
    assetTypes?.forEach((assetType) => {
        switch (assetType) {
            case AssetTypes.ICON:
                UPLOADABLE_ICON_EXTENSION_NAMES.forEach((ext) => results.add(ext));
                break;
            case AssetTypes.AUDIO:
                UPLOADABLE_AUDIO_EXTENSION_NAMES.forEach((ext) => results.add(ext));
                break;
            case AssetTypes.IMAGE:
                UPLOADABLE_IMAGE_EXTENSION_NAMES.forEach((ext) => results.add(ext));
                break;
            case AssetTypes.VIDEO:
                UPLOADABLE_VIDEO_EXTENSION_NAMES.forEach((ext) => results.add(ext));
                break;
            default:
                break;
        }
    });

    const splits: string[] = mimeTypes?.split(',').map((m) => m.trim().toLowerCase()) || [];
    for (const mime of splits) {
        let extensionsOfMime: string[];
        switch (mime) {
            case 'video/*': {
                extensionsOfMime = UPLOADABLE_VIDEO_EXTENSION_NAMES;

                break;
            }
            case 'image/*': {
                extensionsOfMime = UPLOADABLE_IMAGE_EXTENSION_NAMES;

                break;
            }
            case 'audio/*': {
                extensionsOfMime = UPLOADABLE_AUDIO_EXTENSION_NAMES;

                break;
            }
            default: {
                extensionsOfMime = Object.values(VALID_FILE_FORMATS)
                    .flat()
                    .filter((format) => format.mimeType === mime && format.userUploadable)
                    .map((format) => format.uploadDisplayName || format.extension);
            }
        }
        extensionsOfMime.forEach((ext) => results.add(ext));
    }
    if (results.size === 0) {
        throw new Error(
            `No uploadable formats found for file of type: ${assetTypes?.join(', ')} and/or ${mimeTypes}`,
        );
    }
    return [...new Set(results)].join(', ');
}

export const noop: (...args: any[]) => any = () => {
    /* do nothing */
};
export const noopPromise = async () => {
    /* do nothing */
};

export const PLACEHOLDER_IMAGE_URL =
    'https://cdn.lumen5.com/lumen5-site-images/story-placeholder.jpg';

export const AUTO_CROP = true;

// `copy` data
export const COPY_CONTENT = {
    NOT_ENOUGH_DATA: 'Not enough data',
};

// Define in what context the SINGLE_FILE_UPLOAD_SECTION is used
export const SINGLE_FILE_UPLOADER_USAGE_CONTEXT_OPTIONS = {
    WATERMARK: 'Watermark media asset',
    OUTRO_SLIDE: 'Outro media asset', // DEPRECATED, to be merged with MEDIA_ASSET
    MEDIA_ASSET: 'Media asset',
};

export const PROFILE_PLACEHOLDER_IMAGE_URL =
    'https://cdn.lumen5.com/lumen5-site-images/profile-placeholder.png';
export const RECENT_TEMPLATES_CATEGORY = -1;
export const BLUEPRINTS_CATEGORY = -2;

export type TemplateCategoryId = number;

export const CHECKOUT_SESSION_SOURCE_PRICING = 'pricing';
export const CHECKOUT_SESSION_SOURCE_ACCOUNT_BILLING = 'account-billing';

export const COOKIE_KEYS = {
    VOICEOVER_INSTRUCTION: 'voiceover_instruction',
    MEDIA_FILTER: 'media-filter',
    MEDIA_FILTER_RESET: 'media-filter-reset',
    FEEDBACK_TEXT_AI_SHOWN: 'feedback_text_ai_shown_v2',
    FEEDBACK_VOICEOVER_AI_SHOWN: 'feedback_voiceover_ai_shown_v2',
    CAPTIONS: 'captions',
    VIDEO_LIMIT_WARNING: 'video_limit_warning',
    AI_VOICEOVER_DEFAULT_VOICE_PRESET: 'ai_voiceover_default_voice_preset',
    LAST_SELECTED_SCRIPT_SOURCE: 'last_selected_script_source',
    LAST_SELECTED_SCRIPT_DURATION_TARGET: 'last_selected_script_duration_target',
    VIDEO_DURATION_LIMIT_BANNER_DISMISSED: 'video_duration_limit_banner_dismissed',
    THIRD_PARTY_COOKIES_CONSENT: 'third_party_cookies_consent',
    RECENTLY_USED_BLUEPRINT_IDS: 'recently_used_saved_template_ids',
    CUSTOM_TEMPLATE_BANNER_DISMISSED: 'custom_template_banner_dismissed',
    BLUEPRINT_INTRO_BANNER_DISMISSED: 'saved_template_blueprint_rebrand_banner_dismissed',
    MEDIA_PAGE_SOURCES: 'media_page_sources',
    VIDEO_FEEDBACK_FORM_DISMISSED: 'video_feedback_form_dismissed_v2',
    SKETCH_SELECTED_MODEL: 'sketch_selected_model',
    ENTERPRISE_UPSELL_STYLE_TAB_DISMISSED: 'enterprise_upsell_style_tab_dismissed',
    ENTERPRISE_UPSELL_BRAND_KIT_PAGE_DISMISSED: 'enterprise_upsell_brand_kit_page_dismissed',
    ENTERPRISE_UPSELL_ANALYTICS_PAGE_DISMISSED: 'enterprise_upsell_analytics_page_dismissed',
    GENX_ANIMATION_MINUTES_UPSELL_SEEN: 'genx_animation_minutes_upsell_seen',
    GENX_TRIAL_INTRO_SEEN: 'genx_trial_intro_seen',
    GENX_TRIAL_DEPLETED_SEEN: 'genx_trial_depleted_seen',
    GENX_TRIAL_INSUFFICIENT_SEEN: 'genx_trial_insufficient_seen',
    GENX_OPTION_SELECTION_UPSELL_DISMISSED: 'genx_option_selection_upsell_dismissed',
    GENX_SKETCH_UPSELL_DISMISSED: 'genx_sketch_upsell_dismissed',
    GENX_WELCOME_MODAL_SEEN: 'genx_welcome_modal_seen',
    INTEGRATIONS_BANNER_DISMISSED: 'integrations_banner_dismissed',
    NARRATION_MODAL_MUTED: 'narration_modal_muted',
    LAST_DASHBOARD_PATH: 'last_dashboard_path',
    LAST_UPSELL: 'last_upsell',
    ALWAYS_ENFORCE_BLOCK_SEGMENTATION: 'always_enforce_block_segmentation',
    PRODUCT_CALL_CTA_DISMISSED: 'product_call_cta_dismissed',
};

export type TCookieKey = (typeof COOKIE_KEYS)[keyof typeof COOKIE_KEYS];

export const HUBSPOT_BOOK_CHAT_LINK =
    'https://meetings.hubspot.com/lumen5/product-upsells-book-to-learn-more-with-bdrs';

export const DEMO_URL = 'https://lumen5.com/demo/';

export const PRODUCT_TEAM_CALL_URL = 'https://calendar.app.google/ePRUahYoRbY2uTGT7';

export const VIDEO_APPROVAL_STATUS_DISPLAY_NAMES: Record<string, any> = {
    [VideoApprovalStatusEnum.NEEDS_REVIEW]: {
        text: 'Needs review',
        picture_url: 'https://upload.wikimedia.org/wikipedia/commons/2/24/Missing_avatar.svg',
        badgeTheme: 'warning',
    },
    [VideoApprovalStatusEnum.IN_PROGRESS]: {
        text: 'In progress',
        picture_url: 'https://upload.wikimedia.org/wikipedia/commons/2/24/Missing_avatar.svg',
        badgeTheme: 'info',
    },
    [VideoApprovalStatusEnum.APPROVED]: {
        text: 'Approved',
        picture_url: 'https://upload.wikimedia.org/wikipedia/commons/2/24/Missing_avatar.svg',
        badgeTheme: 'success',
    },
};

export enum NEW_VIDEO_FLOW_CREATION_METHOD {
    TEMPLATE = 'template',
    BLUEPRINT = 'blueprint',
    THEME = 'theme',
    TEMPLATE_PREVIEW_MODAL = 'template preview modal',
}

export const DEPRECATED_INVISIBLE_URL =
    'https://storage.googleapis.com/lumen5-prod-images/invisib.png';
export const OUTRO_DUMMY_URL = 'https://cdn.lumen5.com/lumen5-site-images/dummy-image.png';

// Currently when the user clicks the button they would only purchase 1 seat
export const DEFAULT_SEAT_PURCHASE_QUANTITY = 1;

// USER_CHURN_REASONS are used to define reasons which could have prevented users from using lumen5.com.
export const USER_CHURN_REASONS = {
    PENDING_PAYMENT_CONFIRMATION: 'pending_payment_confirmation',
    PENDING_TEAM_SIZE_ADJUSTMENT: 'pending_team_size_adjustment',
};

export const HUBSPOT_MEETING_SCRIPT_TAG_SRC =
    'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
export const HUBSPOT_MEETING_SCRIPT_TAG_ID = 'hubspot-meeting-script-tag-id';

export const MFA_BACKUP_KEYS_SCREEN_MESSAGE =
    'These backup codes will be required if you are no longer able to access the authenticator app. Previous backup codes will no longer work.';

export const BRAND_KITS = {
    MIN_COLORS: 3,
    MAX_COLORS: 10,
    NAME_CHAR_LIMIT: 35,
};

export const LOADING_SCENE_MSG = {
    CREATING_SCENE: 'Creating scene...',
    LOADING_FONT: 'Loading font...',
    GEN_CAPTION: 'Generating captions...',
    OVERLAYS: 'The bots are working hard to make your video.',
    ONBOARDING: 'Generating preview...',
    AI_VOICEOVER_EDIT: "We're regenerating the AI voiceover",
};

export const MEDIA_ZOOM = {
    MIN_VALUE: 0.1,
    MAX_VALUE: 3,
    STEP: 0.01,
};

export const MEDIA_ROTATION = {
    MIN_VALUE: -90,
    MID_VALUE: 0,
    MAX_VALUE: 90,
    STEP: 1,
};

export enum DROPDOWN_ITEM_TEXT_STYLES {
    MUTED = 'muted',
    DANGER = 'danger',
}

export const TRANSCRIPT_CONFIG = {
    LINEBREAK_PAUSE_THRESHOLD_SECONDS: 0.4,
    MAX_PARAGRAPH_SENTENCE_COUNT: 3,
    MAX_TEXT_CORRECT_WORDS: 3,
    MAX_TEXT_CORRECT_NEW_WORDS: 3,
    AUTO_INSERT_START_OFFSET_MILLIS: 4000,
    AUTO_INSERT_DURATION_MILLIS: 5000,
    // The duration threshold when we generate a silence node
    // test-transcript-words has a silence of 1000 ms so it is set to 1001 ms to avoid generating the silence node
    SILENCE_NODE_THRESHOLD_MILLIS: 1001,
    SILENCE_NODE_TARGET_DURATION_MILLIS: 1000,
    MAX_SILENCE_NODES: 9, // Each node is 1s = max duration of 9s
};

export const SHARE_PAGE_NOTIFICATION = {
    RESIZE: 'resize',
    TRANSLATION: 'translation',
};

export const ILLUSTRATION_COLOR_NUMBER = 7; // number of color when we consider icon as illustration and do not allow change colors on it
export const SIMPLE_ICON_COLOR_THRESHOLD = 2; // we consider icon as simple less than this number of colors

export enum SCENE_DURATIONS {
    MIN = 3400,
    MAX = 7000,
    DEFAULT = 4500,
    OUTRO = 5000,
    BRAND = 4053,
}

export const SCRIPT_OPTIONS_TONES = [
    {
        label: 'Match my content',
        value: '',
    },
    {
        label: 'Friendly',
        value: 'friendly',
    },
    {
        label: 'Relaxed',
        value: 'relaxed',
    },
    {
        label: 'Professional',
        value: 'professional',
    },
    {
        label: 'Bold',
        value: 'bold',
    },
    {
        label: 'Adventurous',
        value: 'adventurous',
    },
    {
        label: 'Witty',
        value: 'witty',
    },
    {
        label: 'Persuasive',
        value: 'persuasive',
    },
    {
        label: 'Empathetic',
        value: 'empathetic',
    },
];

export const OverlaySelectorLuminaryConfigOverrides = () => {
    // this is an exported method so that we can override it in tests
    return {
        name: 'overlaySelector',
        mediaSizeToUse: VideoData.THUMBNAIL_MEDIA_SIZE,
        resolutionValue: 220,
        fetchResources: {
            media: true,
            fonts: true,
            transitions: false,
            audio: true,
        },
        rendererDevicePixelRatio: window.devicePixelRatio,
    };
};

export const FullscreenSceneSelectorLuminaryConfigOverrides = () => {
    // this is an exported method so that we can override it in tests
    return {
        name: 'fullscreenSceneSelector',
        resolutionValue: 220,
        fetchResources: {
            media: true,
            fonts: true,
            audio: false,
            transitions: false,
        },
        rendererDevicePixelRatio: window.devicePixelRatio,
    };
};

export const SwapSceneDesignTabLuminaryConfigOverrides = () => {
    // this is an exported method so that we can override it in tests
    return {
        name: 'swapSceneDesignTab',
        mediaSizeToUse: VideoData.THUMBNAIL_MEDIA_SIZE,
        resolutionValue: 220,
        fetchResources: {
            media: true,
            fonts: true,
            transitions: false,
            audio: true,
        },
        rendererDevicePixelRatio: window.devicePixelRatio,
    };
};

/* TYPESCRIPT HELPERS */
export type ValueOf<T> = T[keyof T];

export type DeepReadonly<T> = T extends (...args: never[]) => unknown
    ? T
    : T extends ReadonlyArray<infer Item>
      ? ReadonlyArray<DeepReadonly<Item>>
      : T extends object
        ? { readonly [Key in keyof T]: DeepReadonly<T[Key]> }
        : T;

export const EMPTY_READONLY_ARRAY: readonly never[] = [];

export const withStableEmptyArray = <T>(items: readonly T[]): readonly T[] =>
    items.length === 0 ? EMPTY_READONLY_ARRAY : items;

export const toMutableCopy = <T>(value: DeepReadonly<T>): T => cloneDeep(value) as T;

export type NoOwnProps = Record<never, never>;

export const INVISIBLE_IMAGE_SOURCE_DATA: MediaSourceData = {
    type: 'image',
    title: 'Invisible',
    uuid: '385d4e2f-8b88-41ed-959f-8e8f9da93bba',
    source: 21,
    source_id: 'invisible',
    license: LicenseEnum.CC0,
    is_editorial: false,
    width: 500,
    height: 500,
    thumbnail_url: 'https://storage.googleapis.com/lumen5-prod-images/invisible.png',
    preview_url: 'https://storage.googleapis.com/lumen5-prod-images/invisible.png',
    render_url: 'https://storage.googleapis.com/lumen5-prod-images/invisible.png',
};

export const DEFAULT_STICKER_SOURCE_DATA: MediaSourceData & { attribution: string | null } = {
    uuid: '1e82a6d6-6d92-420f-aa22-8e30692f9a07',
    title: '',
    type: 'video',
    source: 15, // SourceEnum.SOURCE_TENOR
    source_id: '5453549411309774196',
    license: LicenseEnum.CC0,
    is_editorial: false,
    width: 498,
    height: 398,
    thumbnail_url: 'https://media.tenor.com/S67mS6Io5XQAAAAl/flecha.webp',
    preview_url: 'https://media.tenor.com/S67mS6Io5XQAAAAl/flecha.webp',
    render_url: 'https://media.tenor.com/S67mS6Io5XQAAAAl/flecha.webp',
    poster_url: 'https://media.tenor.com/S67mS6Io5XQAAAAl/flecha.webp',
    duration: 0,
    has_audio: false,
    has_voiceover: null,
    audio_url: null,
    attribution: null,
};

export const UNKNOWN_LANGUAGE_CODE = LanguageEnum.XX;

export enum HTTP_STATUS_CODES {
    FORBIDDEN = 403,
    NOT_FOUND = 404,
    CONFLICT = 409,
}

// Avoid changing these values as they are serialized and saved to the database for each video.
export enum SCRIPT_CREATION_SOURCES {
    URL = 'Import an article',
    DOCUMENT = 'Upload a PDF',
    IDEA = 'Outline an Idea',
    PASTE = 'Paste text',
}

export enum AudioVolume {
    MIN = 0,
    MAX = 100,
    DUCK = 15,
}

/**
 * Sometimes we have an enum value but we want the key. For example, for SourceEnum we might have a value of 0
 * and what we want is the key 'SOURCE_USER'. This method will return the key for a given enum value.
 */
export function getEnumKeyByEnumValue(myEnum: Record<string, unknown>, enumValue: unknown): string {
    const keys = Object.keys(myEnum).filter((x) => myEnum[x] === enumValue);
    return keys.length > 0 ? keys[0] : '';
}

/**
 * A helpful filter function to use in a .filter() call to filter out undefined values,
 * and tell typescript what you have left in an array. For example:
 *
 * const arr = [1, undefined, 2, undefined, 3];
 * const filteredArr = arr.filter(isNotUndefined); // filteredArr is number[] automatically
 */
export function isNotUndefined<T>(a: T | undefined): a is T {
    return a !== undefined;
}

export function isUndefined<T>(a: T | undefined): a is undefined {
    return a === undefined;
}
export function isNotNull<T>(a: T | null): a is T {
    return a !== null;
}

// Advanced media controls
export const DEFAULT_BRIGHTNESS = 1;
export const DEFAULT_CONTRAST = 1;
export const DEFAULT_HUE = 0;
export const DEFAULT_SATURATION = 0;
export const DEFAULT_LIGHTNESS = 0;
export const DEFAULT_BLUR = 0;
export const DEFAULT_VIGNETTING = 0;
export const DEFAULT_VIGNETTING_BLUR = 0.3;
export const DEFAULT_VIGNETTING_ALPHA = 1;
export const DEFAULT_NOISE = 0;
export const DEFAULT_NOISE_SIZE = 1;
export const DEFAULT_ENABLE_GREYSCALE = false;
export const DEFAULT_PLAYBACK_RATE = 1;

export function isUserLoggedIn() {
    if (typeof window === 'undefined') return false;
    return window.SERVER && window.SERVER.USER !== null && window.SERVER.ANALYTICS !== null;
}

export class ApiError extends Error {
    body: unknown;
    url: string;
    status: number;

    constructor(status: number, body: unknown, url: string) {
        super(`ApiError: ${status} for ${url}`);
        this.body = body;
        this.status = status;
        this.url = url;
    }
}

export const COLOR_L5_PRIMARY_100 = styles['allColors-color-l5-primary-100'];
export const COLOR_L5_PRIMARY_200 = styles['allColors-color-l5-primary-200'];
export const COLOR_L5_PRIMARY_300 = styles['allColors-color-l5-primary-300'];
export const COLOR_L5_PRIMARY_400 = styles['allColors-color-l5-primary-400'];
export const COLOR_L5_PRIMARY_500 = styles['allColors-color-l5-primary-500'];
export const COLOR_L5_PRIMARY_600 = styles['allColors-color-l5-primary-600'];
export const COLOR_L5_PRIMARY_700 = styles['allColors-color-l5-primary-700'];
export const COLOR_L5_PRIMARY_800 = styles['allColors-color-l5-primary-800'];
export const COLOR_L5_PRIMARY_900 = styles['allColors-color-l5-primary-900'];
export const COLOR_L5_SLATE_NIGHT = styles['allColors-color-l5-slate-night'];
export const COLOR_L5_WARNING_500 = styles['allColors-color-warning-500'];

export const COLOR_L5_GREY_600 = styles['allColors-color-grey-600'];
export const COLOR_L5_GREY_700 = styles['allColors-color-grey-700'];
export const COLOR_L5_GREY_900 = styles['allColors-color-grey-900'];

// A video can have many narrative threads. This function gets the core narrative thread for a video.
// This function is based on get_core_narrative_thread_for_video from blueprint_utils.py
export const getCoreNarrativeThreadForVideo = (
    narrativeThreads: readonly NarrativeThreadEnum[],
) => {
    if (narrativeThreads.includes(NarrativeThreadEnum.VOICEOVER)) {
        return NarrativeThreadEnum.VOICEOVER;
    }
    if (narrativeThreads.includes(NarrativeThreadEnum.TALKING_HEAD)) {
        return NarrativeThreadEnum.TALKING_HEAD;
    }
    return NarrativeThreadEnum.TEXT;
};

export const NARRATIVE_THREAD_ICON_MAP: Record<NarrativeThreadEnum, string> = {
    [NarrativeThreadEnum.TEXT]: 'view_timeline',
    [NarrativeThreadEnum.TALKING_HEAD]: 'person',
    [NarrativeThreadEnum.VOICEOVER]: 'mic',
};

export const NARRATIVE_THREAD_DISPLAY_STRING_MAP: Record<NarrativeThreadEnum, string> = {
    [NarrativeThreadEnum.TEXT]: 'Text on media',
    [NarrativeThreadEnum.TALKING_HEAD]: 'Talking head',
    [NarrativeThreadEnum.VOICEOVER]: 'Voiceover',
};

export const MAX_RECENTLY_USED_SAVED_TEMPLATES = 4;

export const BOOTSTRAP_BREAKPOINT_LG = 992;

export const HELP_DROPDOWN_OPTIONS = {
    learn: {
        id: 'blog',
        href: '/learn/',
        icon: 'school',
        text: 'Learning hub',
    },
    feedback: (onShowFeedbackModal: VoidFunction) => ({
        id: 'feedback',
        icon: 'mail',
        text: 'Submit feedback',
        onClick: onShowFeedbackModal,
    }),
    support: {
        id: 'crisp',
        href: '#',
        icon: 'forum',
        text: 'Contact support',
        onClick: (e: React.MouseEvent) => {
            openCrispChat();
            e.preventDefault();
        },
    },
    help: {
        id: 'help',
        href: 'https://help.lumen5.com',
        target: '_blank',
        icon: 'help',
        text: 'Help center',
    },
    shortcuts: (onShowShortcuts: VoidFunction) => ({
        id: 'shortcuts',
        icon: 'keyboard',
        text: 'Keyboard shortcuts',
        onClick: onShowShortcuts,
    }),
    webinar: {
        id: 'webinar',
        href: 'https://lumen5.com/webinar',
        target: '_blank',
        icon: 'videocam',
        text: 'Register for webinar',
    },
};

export const PUBLISH_BUTTON_ID = 'publish-button';

// Set by the Creator's publish panel when it hands off to the share page for a download that
// needs a finished render. Consumed on mount there.
export const AUTO_DOWNLOAD_PARAM = 'download';
export const CAROUSEL_DOWNLOAD_VALUE = 'carousel';
export const GIF_DOWNLOAD_VALUE = 'gif';

export const BLUEPRINTS_LEARN_MORE_URL = 'https://help.lumen5.com/en/article/blueprints-ea25sk/';

/*
Special cases are used to show the user more scene design options than JUST the list of scene designs
in the template. They are shown in the swap design tab, along with the same scene design as a "None" special
case option (meaning the same scene design is shown multiple times in the swap design tab).

The special cases are:
- None: this is the original scene design
- Multiple speakers: if the user is swapping an overlay on a Talking Head scene, we can show the user a couple
options where multiple MediaAssets have linkToSceneGroup = true, with crop applied to show multiple speakers faces
*/
export enum SceneDesignSpecialCase {
    MultipleSpeakers = 'multiple_speakers',
    SceneSequence = 'scene_sequence',
    Chart = 'chart',
    AnimatedZoom = 'animated_zoom',
    NONE = 'none',
}
// AnimatedZoom is deliberately absent: it renders beside the scene design it duplicates rather
// than in a section of its own.
export const DYNAMIC_DISPLAY_GROUPS = {
    [SceneDesignSpecialCase.MultipleSpeakers]: -1,
    [SceneDesignSpecialCase.SceneSequence]: -2,
    [SceneDesignSpecialCase.Chart]: -3,
};

export const ANIMATED_ZOOM_OPTION_LABEL = 'Animated Zoom';
// Stands in for a scene design id so the option can sit inline next to the design it duplicates.
export const ANIMATED_ZOOM_OPTION_KEY = '__animated_zoom_option__';

export const WATERMARK_ASSET_NAME = 'watermark_1';

export const roleCanCreateVideos = (role: RoleEnum) => {
    const canCreateRoles = [RoleEnum.ADMIN, RoleEnum.EDITOR];
    return canCreateRoles.includes(role);
};

export const roleCanDeleteVideos = (role: RoleEnum) => {
    const canDeleteRoles = [RoleEnum.ADMIN, RoleEnum.EDITOR];
    return canDeleteRoles.includes(role);
};
